import type { RepoState } from "../lib/types";

interface Props {
  repo: RepoState;
  animateLast?: boolean;
}

const LANE_Y: Record<string, number> = { main: 180, feature: 80 };
const LANE_COLOR: Record<string, string> = {
  main: "#FFFFFF",
  feature: "#2D9BD6",
};
const STEP = 100;
const OFFSET_X = 80;

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function labelWidth(name: string, isHead: boolean): number {
  const text = isHead ? `HEAD → ${name}` : name;
  return text.length * 7 + 16;
}

function edgePath(
  p: { x: number; y: number },
  me: { x: number; y: number },
): string {
  if (p.y === me.y) return `M ${p.x} ${p.y} L ${me.x} ${me.y}`;
  const midX = p.x + (me.x - p.x) * 0.55;
  return `M ${p.x} ${p.y} C ${midX} ${p.y}, ${midX} ${me.y}, ${me.x} ${me.y}`;
}

export function GitGraph({ repo, animateLast }: Props) {
  const positions: Record<
    string,
    { x: number; y: number; color: string }
  > = {};
  repo.commits.forEach((c) => {
    positions[c.id] = {
      x: OFFSET_X + c.x * STEP,
      y: LANE_Y[c.branch] ?? 180,
      color: LANE_COLOR[c.branch] ?? "#FFFFFF",
    };
  });

  const maxX = Math.max(...repo.commits.map((c) => c.x), 3);
  const width = OFFSET_X + (maxX + 1) * STEP + 80;
  const height = 280;

  const labels = Object.entries(repo.branches).map(([name, commitId]) => {
    const pos = positions[commitId];
    return {
      name,
      x: pos.x,
      y: pos.y,
      color: LANE_COLOR[name] ?? "#FFFFFF",
      isHead: repo.head === name,
    };
  });

  const stackedLabels: Record<string, typeof labels> = {};
  labels.forEach((l) => {
    const key = `${l.x}-${l.y}`;
    if (!stackedLabels[key]) stackedLabels[key] = [];
    stackedLabels[key].push(l);
  });

  const lastCommitId = repo.commits[repo.commits.length - 1]?.id;
  const orphanSet = new Set(repo.orphanedCommits ?? []);

  return (
    <div className="gf-graph">
      <div className="gf-graph__head">
        <h3>Local repository</h3>
        <div className="gf-graph__head-meta">
          <span>
            branch: <b>{repo.head}</b>
          </span>
          <span>HEAD → {(repo.branches[repo.head] || "").slice(0, 7)}</span>
        </div>
      </div>

      <div className="gf-graph__body">
        <svg
          className="gf-graph__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
        >
          <line
            x1={0}
            y1={LANE_Y.main}
            x2={width}
            y2={LANE_Y.main}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
          <line
            x1={0}
            y1={LANE_Y.feature}
            x2={width}
            y2={LANE_Y.feature}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />

          <text
            x={20}
            y={LANE_Y.main + 4}
            fill="rgba(255,255,255,0.35)"
            fontSize="11"
            fontFamily="ui-monospace, Menlo"
          >
            main
          </text>
          {repo.commits.some((c) => c.branch === "feature") && (
            <text
              x={20}
              y={LANE_Y.feature + 4}
              fill="rgba(45,155,214,0.55)"
              fontSize="11"
              fontFamily="ui-monospace, Menlo"
            >
              feature
            </text>
          )}

          {repo.commits.map((c) => {
            if (!c.parent) return null;
            const p = positions[c.parent];
            const me = positions[c.id];
            if (!p || !me) return null;
            const isLast = c.id === lastCommitId && animateLast;
            const isOrphan = orphanSet.has(c.id) || orphanSet.has(c.parent);
            return (
              <path
                key={`e-${c.id}`}
                d={edgePath(p, me)}
                stroke={me.color}
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={isOrphan ? "5 4" : undefined}
                opacity={isOrphan ? 0.35 : 1}
                className={isLast ? "gf-graph__edge-enter" : undefined}
              />
            );
          })}

          {/* Second-parent edges for merge commits */}
          {repo.commits.map((c) => {
            if (!c.parent2) return null;
            const p = positions[c.parent2];
            const me = positions[c.id];
            if (!p || !me) return null;
            return (
              <path
                key={`e2-${c.id}`}
                d={edgePath(p, me)}
                stroke={LANE_COLOR[c.branch] ?? "#fff"}
                strokeWidth="2"
                strokeDasharray="4 3"
                fill="none"
                strokeLinecap="round"
                opacity={0.85}
              />
            );
          })}

          {repo.commits.map((c) => {
            const pos = positions[c.id];
            const isLast = c.id === lastCommitId && animateLast;
            const isOrphan = orphanSet.has(c.id);
            return (
              <g
                key={c.id}
                className={isLast ? "gf-graph__commit-enter" : undefined}
                opacity={isOrphan ? 0.4 : 1}
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="11"
                  fill={pos.color === "#FFFFFF" ? "#fff" : pos.color}
                  stroke={c.branch === "main" ? "#fff" : pos.color}
                  strokeWidth="2"
                />
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="4"
                  fill={c.branch === "main" ? "#001847" : "#fff"}
                />
                <text
                  x={pos.x}
                  y={pos.y + (c.branch === "main" ? 32 : -22)}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.5)"
                  fontSize="10"
                  fontFamily="ui-monospace, Menlo"
                >
                  {c.id.slice(0, 7)}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + (c.branch === "main" ? 46 : -36)}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.75)"
                  fontSize="11"
                  fontFamily="Raleway, sans-serif"
                >
                  {truncate(c.msg, 22)}
                </text>
              </g>
            );
          })}

          {Object.values(stackedLabels).map((group) =>
            group.map((l, idx) => {
              const labelX = l.x + 22;
              const labelY = l.y + idx * 26 - (group.length - 1) * 13;
              return (
                <g key={`lbl-${l.name}`}>
                  <rect
                    x={labelX}
                    y={labelY - 11}
                    width={labelWidth(l.name, l.isHead)}
                    height="22"
                    rx="11"
                    fill={l.isHead ? l.color : "transparent"}
                    stroke={l.color}
                    strokeWidth="1.5"
                  />
                  <text
                    x={labelX + 10}
                    y={labelY + 4}
                    fill={
                      l.isHead
                        ? l.color === "#FFFFFF"
                          ? "#001847"
                          : "#fff"
                        : l.color
                    }
                    fontSize="11"
                    fontFamily="ui-monospace, Menlo"
                    fontWeight="600"
                  >
                    {l.isHead ? `HEAD → ${l.name}` : l.name}
                  </text>
                </g>
              );
            }),
          )}
        </svg>
      </div>

      <div className="gf-graph__legend">
        <div className="gf-graph__legend-item">
          <div
            className="gf-graph__legend-dot"
            style={{ background: "#fff" }}
          ></div>
          <span>main commit</span>
        </div>
        <div className="gf-graph__legend-item">
          <div
            className="gf-graph__legend-dot"
            style={{ background: "#2D9BD6" }}
          ></div>
          <span>feature commit</span>
        </div>
        <div className="gf-graph__legend-item" style={{ marginLeft: "auto" }}>
          <span style={{ fontFamily: "ui-monospace, Menlo" }}>
            {repo.commits.length} commits
          </span>
        </div>
      </div>
    </div>
  );
}
